import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Info, MousePointerClick, Eye, Users, Share2, LogIn, Server } from 'lucide-react';
import { toast } from 'sonner';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsPageHeader,
  AnalyticsRefreshControl,
  ChartCard,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { useAnalyticsAutoRefresh } from '@/hooks/useAnalyticsAutoRefresh';
import { ANALYTICS_COLORS, formatNumber, formatUtcRange } from '@/components/analytics/analyticsDisplay';
import { getAnalyticsDateRange } from '@/services/analyticsMetrics';
import {
  CreatorAnalyticsRange,
  CreatorSummaryRow,
  getCreatorAnalytics,
} from '@/services/creatorCodesService';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/i18n';

export default function CreatorAnalytics() {
  const { t } = useLanguage();
  const [range, setRange] = useState<CreatorAnalyticsRange>('7d');
  const [rows, setRows] = useState<CreatorSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `silent` refetches update the data in place without the full-page spinner,
  // used by auto-refresh (interval + tab focus) and the manual Refresh button.
  const loadAnalytics = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const result = await getCreatorAnalytics(range);
      setRows(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('analyticsLoadFailed');
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const silentRefresh = useCallback(() => loadAnalytics(true), [loadAnalytics]);
  useAnalyticsAutoRefresh(silentRefresh);

  const dateRange = useMemo(() => getAnalyticsDateRange(range), [range]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.serverEntrySessions += row.serverEntrySessions;
        acc.serverEntryRequests += row.serverEntryRequests;
        acc.attributedSessions += row.attributedSessions;
        acc.pageViews += row.pageViews;
        acc.productClicks += row.productClicks;
        acc.affiliateClicks += row.affiliateClicks;
        return acc;
      },
      {
        serverEntrySessions: 0,
        serverEntryRequests: 0,
        attributedSessions: 0,
        pageViews: 0,
        productClicks: 0,
        affiliateClicks: 0,
      },
    );
  }, [rows]);

  const activeRows = useMemo(
    () => rows.filter((row) => row.serverEntryRequests > 0 || row.attributedSessions > 0 || row.pageViews > 0),
    [rows],
  );

  const topBySessions = useMemo(
    () =>
      [...rows]
        .filter((row) => row.attributedSessions > 0)
        .sort((a, b) => b.attributedSessions - a.attributedSessions)
        .slice(0, 12),
    [rows],
  );

  const hasAnyCreator = rows.length > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsPageHeader
        title={t('creatorAnalyticsTitle')}
        description={t('creatorAnalyticsDescription')}
        range={range}
        onRangeChange={setRange}
        rangeLabel={t('analyticsDateRange')}
      >
        <p className="mt-2 text-xs text-muted-foreground">
          {t('analyticsUtcRange', { range: formatUtcRange(dateRange.start, dateRange.end) })}
        </p>
        <AnalyticsRefreshControl
          refreshLabel={t('analyticsRefresh')}
          hintLabel={t('analyticsAutoRefreshHint')}
          refreshing={refreshing}
          disabled={loading}
          onRefresh={silentRefresh}
        />
      </AnalyticsPageHeader>

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>{t('creatorDisclaimerTitle')}</AlertTitle>
        <AlertDescription>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
            <li>{t('creatorDisclaimerServerEntries')}</li>
            <li>{t('creatorDisclaimerFirstParty')}</li>
            <li>{t('creatorDisclaimerBrowserGap')}</li>
            <li>{t('creatorDisclaimerRedirects')}</li>
            <li>{t('creatorDisclaimerPurchases')}</li>
          </ul>
        </AlertDescription>
      </Alert>

      {loading ? (
        <AnalyticsLoadingState label={t('analyticsLoading')} />
      ) : error ? (
        <AnalyticsErrorState
          title={t('analyticsErrorTitle')}
          message={error}
          retryLabel={t('analyticsRetry')}
          onRetry={() => loadAnalytics()}
        />
      ) : !hasAnyCreator ? (
        <AnalyticsEmptyState title={t('creatorEmptyTitle')} description={t('creatorReportEmptyDescription')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <MetricCard
              title={t('creatorMetricServerEntrySessionSum')}
              value={formatNumber(totals.serverEntrySessions)}
              description={t('creatorMetricServerEntrySessionSumDescription')}
              icon={LogIn}
            />
            <MetricCard
              title={t('creatorMetricServerEntryRequests')}
              value={formatNumber(totals.serverEntryRequests)}
              description={t('creatorMetricServerEntryRequestsDescription')}
              icon={Server}
            />
            <MetricCard
              title={t('creatorMetricSessions')}
              value={formatNumber(totals.attributedSessions)}
              description={t('creatorMetricSessionsDescription')}
              icon={Users}
            />
            <MetricCard
              title={t('creatorMetricPageViews')}
              value={formatNumber(totals.pageViews)}
              description={t('creatorMetricPageViewsDescription')}
              icon={Eye}
            />
            <MetricCard
              title={t('creatorMetricProductClicks')}
              value={formatNumber(totals.productClicks)}
              description={t('creatorMetricProductClicksDescription')}
              icon={MousePointerClick}
            />
            <MetricCard
              title={t('creatorMetricAffiliateClicks')}
              value={formatNumber(totals.affiliateClicks)}
              description={t('creatorMetricAffiliateClicksDescription')}
              icon={Share2}
            />
          </div>

          {topBySessions.length > 0 && (
            <ChartCard title={t('creatorChartTitle')} description={t('creatorChartDescription')}>
              <div className="h-[320px]" role="img" aria-label={t('creatorChartTitle')}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topBySessions} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="code" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => [formatNumber(value), t('creatorMetricSessions')]} />
                    <Bar dataKey="attributedSessions" name={t('creatorMetricSessions')} fill={ANALYTICS_COLORS[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}

          <ChartCard title={t('creatorTablePerformanceTitle')} description={t('creatorTablePerformanceDescription')}>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('creatorTableCode')}</TableHead>
                    <TableHead>{t('creatorTableLabel')}</TableHead>
                    <TableHead>{t('creatorTableStatus')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricServerEntrySessions')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricServerEntryRequests')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricSessions')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricPageViews')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricProductClicks')}</TableHead>
                    <TableHead className="text-right">{t('creatorMetricAffiliateClicks')}</TableHead>
                    <TableHead className="text-right">{t('analyticsAgentSelections')}</TableHead>
                    <TableHead className="text-right">{t('analyticsSearches')}</TableHead>
                    <TableHead className="text-right">{t('analyticsZeroResultSearches')}</TableHead>
                    <TableHead className="text-right">{t('creatorTableFilters')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.code}>
                      <TableCell className="font-mono font-medium">{row.code}</TableCell>
                      <TableCell>{row.label || '--'}</TableCell>
                      <TableCell>
                        {!row.isRegistered ? (
                          <Badge variant="destructive">{t('creatorStatusUnregistered')}</Badge>
                        ) : row.enabled ? (
                          <Badge variant="default">{t('creatorEnabled')}</Badge>
                        ) : (
                          <Badge variant="secondary">{t('creatorDisabled')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(row.serverEntrySessions)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.serverEntryRequests)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.attributedSessions)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.pageViews)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.productClicks)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.affiliateClicks)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.agentSelections)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.searches)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.zeroResultSearches)}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.filters)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {activeRows.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">{t('creatorNoActivityYet')}</p>
            )}
          </ChartCard>
        </>
      )}
    </div>
  );
}
